import * as angular from 'angular';
import * as d3 from 'd3';
import * as _ from 'lodash';

import { ComponentDefinition, Dashboard, Group, AlarmList, Identifier, IGridsterOptions, DigitalSetSimpleDictionary, IValueViewModel, Dictionary, WidgetValues, TagStringValueDto } from '../../models';
import { IGroupSelection, IMaximizable, IWidget, IZoomableWithLogbookSelectionWidget } from '../../widgets';
import { GridsterOptions } from '../../states/gridsterOptions';
import { guidEmpty, editWidgetState, guidGroupValidationPageLink, Roles } from '../../common/constants';
import { groupSelected, toggleMaximizeWidget } from '../../common/events';
import { WideError, toDictionary, getError, getWidgetClass } from '../../common';
import { IRootScope } from '../../IRootScope';
import { ILoggerService, IDashboardLoadService, IExportSvgService, IDateRangeService, IDashboardHelperService, ILogbookService, IAuthorizationService, ILogbookModalSeries } from '../../services';
import { ILogbookSelection, IZoomControl } from '../../widgets/models/interfaces';
import { LogbookTableWidget } from '../../widgets/widgets.index';

class WidgetsListController implements angular.IComponentController, angular.IOnChanges {
    beginDate;
    dashboardGuid: string;
    dashboardName: string;
    dashboardNames: _.Dictionary<string>;
    endDate;
    error: WideError;
    guid: string;
    hover: { [guid: string]: boolean };
    isEditMode: boolean;
    isOutsideWindow: boolean;
    loadingStatusList: any[];
    options: IGridsterOptions;
    redirect: { url: string; params: any };
    showButtons: { [guid: string]: boolean };

    readonly alarmList: AlarmList;
    readonly boundData: WidgetValues[];
    readonly canNavigate: boolean;
    readonly dashboards: Dashboard[];
    readonly digitalSetDictionary: DigitalSetSimpleDictionary;
    readonly groups: Group[];
    readonly identifiers: Identifier[];
    readonly isDirty: boolean;
    readonly itemGuid: string;
    readonly selectedGroup: Group;
    readonly selectedItem: IWidget;
    readonly stringValues: TagStringValueDto[];
    readonly viewModels: Dictionary<IValueViewModel>;
    readonly widgetList: IWidget[];

    private _garbages: (() => void)[] = [];
    private _hasManualInputRoles: boolean;
    private maximizedGuid: string;

    static $inject = [
        '$scope',
        '$rootScope',
        '$translate',
        '$state',
        '$stateParams',
        'dateRangeService',
        'exportSvgService',
        'dashboardLoadService',
        'loggerService',
        'dashboardHelperService',
        'logbookService',
        'authorizationService',
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$translate: angular.translate.ITranslateService,
        private _$state: angular.ui.IStateService,
        private _$stateParams: angular.ui.IStateParamsService,
        private _dateRangeService: IDateRangeService,
        private _exportSvgService: IExportSvgService,
        private _dashboardLoadService: IDashboardLoadService,
        private _loggerService: ILoggerService,
        private _dashboardHelperService: IDashboardHelperService,
        private _logbookService: ILogbookService,
        private _authorizationService: IAuthorizationService
    ) {
    }

    $onInit() {
        this._hasManualInputRoles = this._authorizationService.isInRoles(Roles.manualInputRoles);
        if (!this.isEditMode) {
            this.isEditMode = false;
        }
        this.hover = {};
        if (!this.options) {
            this.options = new GridsterOptions().options();
        }
        this.loadingStatusList = [];

        this.maximizedGuid = this._$stateParams['maximized'];

        this._$scope.$on(groupSelected, (event, group) => {
            this.guid = group.guid;
        });

        if (this.maximizedGuid) {

            this._garbages.push(this._$scope.$watch(() => this.widgetList, () => {
                if (this.maximizedGuid && this.maximizedGuid !== 'false' && this.widgetList && this.widgetList.length) {
                    const found = _.find(this.widgetList, w => w.guid === this.maximizedGuid) as unknown as IMaximizable;
                    if (found) {
                        found.isMaximized = true;
                        this.isOutsideWindow = true;
                        this._$rootScope.$broadcast(toggleMaximizeWidget, { isMaximized: found.isMaximized });
                    }
                }
            }));
        }
        this.computeShowButtons();
        this.computeLinkIcons();
    }

    $onDestroy() {
        this._garbages.forEach(g => g());
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['boundData']?.currentValue) {
            this._dashboardLoadService.assignValues(this.boundData, this.widgetList);
        }
        if (changesObj['dashboards']?.currentValue) {
            if (this.dashboards && this.dashboards.length > 0) {
                this.dashboardNames = this._dashboardHelperService.getDashboardNames(this.dashboards);
            }
        }
        // will cover for editmode and widgetlist changes
        this.computeShowButtons();
        this.computeLinkIcons();
        this.disableLogbookSelection();
    }

    isLoading(item): boolean {
        const status = this.getStatus(item);
        return !!status && status.isLoading;
    }

    loadingMessage(item): string {
        const status = this.getStatus(item);
        return status ? status.message : 'Loading';
    }

    getStatus(item) {
        const status = _.find(this.loadingStatusList, l => l.guid === item.guid);
        return status;
    }

    addStatus(item) {
        const status = {
            guid: item.guid,
            isLoading: false,
            message: 'Loading'
        };
        this.loadingStatusList.push(status);
        return status;
    }

    exportToCsv(item: any): void {
        let status = this.getStatus(item);
        if (!status) {
            status = this.addStatus(item);
        }
        status.isLoading = true;
        status.message = this._$translate.instant('Exporting data to CSV');
        try {
            item.exportToCsv(this.guid, item.data, this.viewModels)
                .then(() => status.isLoading = false, () => status.isLoading = false)
                .catch(() => status.isLoading = false);
        } catch (e) {
            status.isLoading = false;
        }
    }

    isExportPng(): boolean {
        return this._exportSvgService.isExportPng();
    }

    isExportSvg(): boolean {
        return this._exportSvgService.isExportSvg();
    }

    exportToPng($event: angular.IAngularEvent, item: IWidget): angular.IPromise<any> {
        const svgElement = this.getSvgInWidget(item.guid);
        return this._exportSvgService.exportSvgAsPng(item.name, svgElement);
    }

    exportToSvg($event: angular.IAngularEvent, item: IWidget): angular.IPromise<any> {
        const svgElement = this.getSvgInWidget(item.guid);
        return this._exportSvgService.exportSvg(item.name, svgElement);
    }

    getClass(item: any): any {
        const result = getWidgetClass(item);
        result['bordered'] = this.selectedItem === item;
        return result;
    }

    go(item) {
        if (this.canNavigate === false) {
            const msg = this._$translate.instant('Save or cancel changes before selecting another widget.');
            this._loggerService.logWarning(msg);
        } else {
            if (this.redirect) {
                this._$state.go(this.redirect.url, this.redirect.params(item));
            } else {
                this._$state.go(editWidgetState, this.editParams(item));
            }
        }
    }

    editParams(item) {
        const beginDate = this._dateRangeService.initialized()
            ? this._dateRangeService.beginDate() as string
            : this.beginDate;
        const endDate = this._dateRangeService.initialized()
            ? this._dateRangeService.endDate() as string
            : this.endDate;
        return {
            dashboardGuid: this.dashboardGuid,
            widgetGuid: item.guid,
            beginDate: beginDate,
            endDate: endDate
        };
    }

    private displayOverlay(item: any): boolean {
        const display: boolean = !!(item.isZoomEnabled
            || item.isZoomable
            || (item.isMaximizable && item.isMaximized && item.isDetachable !== false)
            || item.baseType === 'badActors'
            || (this.isEditMode && (item.canDuplicate === undefined || !!item.canDuplicate))
            || item.isExportable
            || (this.isEditMode && (!item.canDelete || item.canDelete()))
            || (this.isEditMode && item.hasExtendedProperties)
            || (this.isEditMode && item.type === 'scatter')
            || (!this.isEditMode && item.getError))
            || (item.error)
            || (item.dashboardLink);
        return display;
    }

    watchKeyPress($event) {
        if ($event.keyCode === 27) {
            const maximized = this.widgetList.filter((item: any) => (item.isMaximized
                && !item.isDisabledMaximized));
            if (maximized.length) {
                maximized.forEach((i: any) => i.isMaximized = false);
                $event.preventDefault();
            }
        }
    }

    showDashboardLink(guid: string): boolean {
        return (guid && guid !== guidEmpty && !this.isEditMode);
    }

    gotoDestination(guid: string, item: IGroupSelection): void {
        if (guid && guid !== guidEmpty) {
            const selectedGuid = this._dashboardHelperService.getSelectedGroupGuid(this.selectedGroup?.guid, item);
            const beginDate = this._dateRangeService.beginDate() as string;
            const endDate = this._dateRangeService.endDate() as string;
            const go = this._dashboardHelperService.getGotoDestination(guid, toDictionary(this.dashboards), selectedGuid, beginDate, endDate);
            this._$state.go(go.route, go.params);
        }
    }

    preventPropagation($event: angular.IAngularEvent) {
        $event.stopPropagation();
        $event.preventDefault();
    }

    isAlarmBadgeDisplayed(item: IWidget): boolean {
        return !this.isEditMode && (!!item.getError || !!item.error);
    }

    getError(item?: IWidget) {
        return getError(item);
    }

    onHover(item: IWidget) {
        this.hover[item.guid] = true;
        this.computeShowButtons();
    }

    onMouseOut(item: IWidget) {
        this.hover[item.guid] = false;
        this.computeShowButtons();
    }

    toggleSelection(item: IZoomableWithLogbookSelectionWidget) {
        item.isSelectingLogbook = !item.isSelectingLogbook;
        item.select();
    }

    openModal(item: LogbookTableWidget) {
        const series = (item.configuration?.series?.length > 0 ?? false)
            ? item.configuration.series.map<ILogbookModalSeries>(s => ({ isBoundToGroup: s.isBoundToGroup, vm: this.viewModels[s.guid] }))
            : [];
        this._logbookService.openModal(
            undefined,
            { severity: 0 },
            false,
            this._hasManualInputRoles,
            true,
            { startDate: this._dateRangeService.beginDate() as string, endDate: this._dateRangeService.endDate() as string },
            undefined,
            undefined,
            item.prefilters,
            item.allowedGroupGuidList,
            series,
            item.configuration.taskOptionsOverride,
            item.configuration.launchTask
        );
    }

    private getSvgInWidget(guid: string): SVGSVGElement {
        let svgElement: any = d3.select(`#item${guid}`).select('svg').node();
        if (svgElement.length) {
            svgElement = svgElement[0];
        }
        return svgElement;
    }

    private computeShowButtons() {
        const result = {};
        this.widgetList.forEach(w => {
            const show = this.isDirty
                ? this.selectedItem === w
                : this.hover[w.guid] || this.isEditMode;
            result[w.guid] = show && this.displayOverlay(w);
        });
        this.showButtons = result;
    }

    private computeLinkIcons() {
        this.widgetList.forEach(w => {
            w.linkIcon = (w.dashboardLink === guidGroupValidationPageLink)
                ? 'fa-check-square-o'
                : 'fa-th-large';
        });
    }

    private disableLogbookSelection() {
        if (this.isEditMode) {
            this.widgetList
                .filter((w: IWidget & ILogbookSelection) => w.isSelectingLogbook)
                .forEach((w: IWidget & ILogbookSelection & IZoomControl) => this.toggleSelection(w));
        }
    }
}

export const wtWidgetsList: ComponentDefinition = {
    componentId: 'wtWidgetsList',
    controller: WidgetsListController,
    template: require('./widgetsList.component.html'),
    controllerAs: 'vm',
    bindings: {
        alarmList: '<',
        beginDate: '=',
        boundData: '<?',
        boundMode: '<?',
        canNavigate: '<?',
        connectionPointsDictionary: '<?',
        dashboards: '<',
        dashboardGuid: '=',
        digitalSetDictionary: '<?',
        duplicateItem: '&',
        endDate: '=',
        error: '=?',
        getClass: '&?',
        getErrorLevel: '&',
        groups: '<',
        identifiers: '<',
        isAlarmed: '&',
        isDirty: '<',
        isEditMode: '<',
        isLoaded: '<?',
        itemGuid: '<?',
        options: '=',
        redirect: '=',
        selectedGroup: '<?',
        selectItem: '&',
        selectedItem: '<',
        tagsDictionary: '<?',
        stringValues: '<',
        viewModels: '<?',
        widgetList: '<',
    }
};